/**
 * MarkdownContent (Sprint 4 B4) — render markdown an toàn cho lesson content.
 *
 * Stack:
 *   - `react-markdown@9` — core renderer (parser remark → react)
 *   - `remark-gfm` — GitHub Flavored Markdown (tables, strikethrough, checklists, autolinks)
 *   - `rehype-sanitize` — XSS protection, whitelist tags + attrs
 *   - `remark-math` + `rehype-katex` — công thức toán `$...$` (inline) / `$$...$$`
 *     (khối), cùng cú pháp với RichMarkdownEditor ở trang sửa bài học. KaTeX chạy
 *     SAU sanitize (chỉ sinh HTML từ chuỗi LaTeX, `trust` mặc định false).
 *
 * Security:
 *   - Strip <script>, <iframe>, inline event handlers (on*), `style` attr
 *   - <a> tag bị force `target="_blank" rel="noopener noreferrer"` để chống tab-nabbing
 *   - Image src whitelist scheme http/https (block javascript:, data:)
 *
 * Defer Sprint 5+:
 *   - `react-syntax-highlighter` cho code block highlight (+80kb, không essential)
 *   - Mermaid diagrams (cho khóa thiết kế hệ thống)
 */

import { Typography } from "antd";
import "katex/dist/katex.min.css";
import ReactMarkdown from "react-markdown";
import rehypeKatex from "rehype-katex";
import rehypeSanitize, { defaultSchema } from "rehype-sanitize";
import remarkGfm from "remark-gfm";
import remarkMath from "remark-math";

const { Title, Paragraph, Text, Link } = Typography;

// Whitelist schema — extend default để allow class trên <code> (cho syntax highlight
// Sprint 5+) + target/rel trên <a>.
const sanitizeSchema = {
  ...defaultSchema,
  attributes: {
    ...defaultSchema.attributes,
    code: [...(defaultSchema.attributes?.code ?? []), "className"],
    a: [...(defaultSchema.attributes?.a ?? []), "target", "rel"],
  },
};

type MdNode = { type: string; value?: string; children?: MdNode[] };

/**
 * Xuống dòng đơn → `<br>` (như remark-breaks, không thêm dependency). Dùng cho câu trả lời của
 * sinh viên: bài cũ gõ ở textarea thường chỉ xuống dòng 1 lần — markdown chuẩn sẽ dồn thành 1 dòng.
 * Chỉ tách node `text` → không đụng code / công thức.
 */
function remarkSoftBreaks() {
  const walk = (node: MdNode) => {
    if (!node.children) return;
    node.children = node.children.flatMap((child): MdNode[] => {
      if (child.type !== "text" || !child.value?.includes("\n")) {
        walk(child);
        return [child];
      }
      return child.value
        .split(/\r?\n/)
        .flatMap((part, i): MdNode[] => [
          ...(i > 0 ? [{ type: "break" }] : []),
          ...(part ? [{ type: "text", value: part }] : []),
        ]);
    });
  };
  return (tree: MdNode) => walk(tree);
}

interface Props {
  /** Raw markdown source string. */
  source: string;
  /**
   * Hiển thị câu trả lời của sinh viên đúng như lúc gõ: giữ xuống dòng đơn (bài cũ gõ ở textarea)
   * và khoảng trắng / thụt lề trong đoạn văn (vd code gõ thẳng vào đoạn văn thay vì khối code —
   * editor của SV hiển thị có thụt lề, nên trang chấm cũng phải giữ).
   */
  breaks?: boolean;
}

export function MarkdownContent({ source, breaks = false }: Props) {
  return (
    <div className="sep2026-markdown" data-testid="markdown-content" style={{ lineHeight: 1.7 }}>
      <ReactMarkdown
        remarkPlugins={breaks ? [remarkGfm, remarkMath, remarkSoftBreaks] : [remarkGfm, remarkMath]}
        rehypePlugins={[[rehypeSanitize, sanitizeSchema], rehypeKatex]}
        components={{
          // Map markdown → AntD Typography để đồng bộ theme + dark mode sau này
          h1: ({ children }) => <Title level={2}>{children}</Title>,
          h2: ({ children }) => <Title level={3}>{children}</Title>,
          h3: ({ children }) => <Title level={4}>{children}</Title>,
          h4: ({ children }) => <Title level={5}>{children}</Title>,
          p: ({ children }) => (
            <Paragraph style={{ marginBottom: 12, whiteSpace: breaks ? "pre-wrap" : undefined }}>
              {children}
            </Paragraph>
          ),
          a: ({ href, children }) => (
            <Link href={href} target="_blank" rel="noopener noreferrer">
              {children}
            </Link>
          ),
          code: ({ className, children, ...props }) => {
            // Inline code: `<code>` không có className `language-*`
            const isInline = !className?.startsWith("language-");
            if (isInline) {
              return (
                <Text
                  code
                  style={{
                    fontSize: "0.9em",
                    padding: "2px 6px",
                  }}
                  {...props}
                >
                  {children}
                </Text>
              );
            }
            // Block code: `<pre><code class="language-x">` → fallback đơn giản
            return (
              <code className={className} {...props}>
                {children}
              </code>
            );
          },
          pre: ({ children }) => (
            <pre
              style={{
                background: "#f5f5f5",
                padding: 16,
                borderRadius: 8,
                overflow: "auto",
                fontSize: "0.9em",
              }}
            >
              {children}
            </pre>
          ),
          table: ({ children }) => (
            <div style={{ overflowX: "auto", marginBottom: 16 }}>
              <table
                style={{
                  borderCollapse: "collapse",
                  width: "100%",
                }}
              >
                {children}
              </table>
            </div>
          ),
          th: ({ children }) => (
            <th
              style={{
                border: "1px solid #d9d9d9",
                padding: "8px 12px",
                background: "#fafafa",
                textAlign: "left",
              }}
            >
              {children}
            </th>
          ),
          td: ({ children }) => (
            <td
              style={{
                border: "1px solid #d9d9d9",
                padding: "8px 12px",
              }}
            >
              {children}
            </td>
          ),
          blockquote: ({ children }) => (
            <blockquote
              style={{
                borderLeft: "4px solid #1677ff",
                paddingLeft: 16,
                marginLeft: 0,
                color: "#7a8499",
              }}
            >
              {children}
            </blockquote>
          ),
        }}
      >
        {source}
      </ReactMarkdown>
    </div>
  );
}
